import { AgenturModel } from 'app/models/agentur.model';
import { AgenturStore } from '../agentur.store';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import _ from 'lodash';
import { BaseUiStore } from './base.ui.store';
import { KvAgtWkzgKofferFzwStore } from '../kv.agt.wkzgkoffer.fzw.store';
import { KvAgtWkzgKofferBakStore } from '../kv.agt.wkzgkoffer.bak.store';
import { KvAgtMsLiteModel } from 'app/models/kv.agt.ms.lite.model';
import { countArray } from 'app/utils';

export class FzwModel {
	agt: AgenturModel;
	neuKaufpreisentschaedigung: number = 0;
	sbVerzicht: number = 0;
	wertgutschein: number = 0;
	hasWkzg: boolean = false;
	sumKontingent: number = 0;
	preisgarant24Monate: number = 0;

	constructor(agt: AgenturModel, ms?: KvAgtMsLiteModel) {
		this.agt = agt;
		if (ms && ms.j) {
			const val = JSON.parse(ms.j);
			this.neuKaufpreisentschaedigung = val.n;
			this.sbVerzicht = val.s;
			this.wertgutschein = val.w;
			this.preisgarant24Monate = val.p;
			this.hasWkzg = true;
			this.sumKontingent = countArray([this.neuKaufpreisentschaedigung, this.sbVerzicht, this.wertgutschein]);
		}
	}
}

export class BakModel {
	agt: AgenturModel;
	year: number = 0;
	nachtragsliste: string = '';
	wertgut30Eur: number = 0;
	restWertgut: number = 0;
	downgrade: number = 0;
	restDowngrade: number = 0;
	sbverzicht: number = 0;
	restSbverz: number = 0;
	nachhaltigkeitsbonus: number = 0;
	restNachhbo: number = 0;
	preisgarantie: number = 0;
	restPreisg: number = 0;
	sumKontingent: number = 0;
	sumRest: number = 0;
	hasWkzg: boolean = false;
	bakVerzicht: number = 0;
	restBakVerz: number = 0;
	joker: number = 0;
	jokerRest: number = 0;
	bakAvg: number = 0;
	// TEMP debug fields to analyze inconsistent sum_kontingent in the 2026 delivery - remove later
	// sumKontingentDelivered: number = 0;
	// skIsInconsistent: boolean = false;

	constructor(agt: AgenturModel, ms?: KvAgtMsLiteModel) {
		this.agt = agt;
		this.year = ms?.bm ? ms.bm : 0;

		if (ms && ms.j) {
			const val = JSON.parse(ms.j);
			this.nachtragsliste = val.nl || '';
			this.wertgut30Eur = val.w3 || 0;
			this.restWertgut = val.rw || 0;
			this.downgrade = val.dg || 0;
			this.restDowngrade = val.rd || 0;
			this.sbverzicht = val.sv || 0;
			this.restSbverz = val.rs || 0;
			this.nachhaltigkeitsbonus = val.nb || 0;
			this.restNachhbo = val.rn || 0;
			this.preisgarantie = val.pg || 0;
			this.bakVerzicht = val.bvz || 0;
			this.restBakVerz = val.rbvz || 0;
			this.restPreisg = val.rp || 0;
			this.joker = val.j || 0;
			this.jokerRest = val.jr || 0;
			this.sumRest = countArray([this.restDowngrade, this.restNachhbo, this.restPreisg, this.restSbverz, this.restWertgut, this.jokerRest]);

			// 'sk' from the data delivery is unreliable (in 2026 it contains the rest sum instead of the
			// total kontingent for ~2000 agencies), so we calculate the total from the individual tool kontingents
			this.sumKontingent = countArray([this.wertgut30Eur, this.downgrade, this.sbverzicht, this.nachhaltigkeitsbonus, this.preisgarantie, this.joker]);

			this.hasWkzg = true;
			this.bakAvg = this.sumKontingent > 0 ? Math.floor(((this.sumKontingent - this.sumRest) / this.sumKontingent) * 100) : 0;

			// TEMP debug fields to analyze inconsistent sum_kontingent in the 2026 delivery - remove later
			// this.sumKontingentDelivered = val.sk || 0;
			// this.skIsInconsistent = this.sumKontingentDelivered !== this.sumKontingent;
		}
	}
}

export class WkzgKofferVM {
	agt: AgenturModel;
	@observable
	fzw: FzwModel;
	@observable
	bak: BakModel;
	year: number;

	constructor(agt: AgenturModel, fzwMs?: KvAgtMsLiteModel, bakMs?: KvAgtMsLiteModel, year?: number) {
		makeObservable(this);
		this.agt = agt;
		this.fzw = new FzwModel(agt, fzwMs);
		this.bak = new BakModel(agt, bakMs);
		this.year = year!;
	}
}

export class AgtWkzkofferUiStore extends BaseUiStore<WkzgKofferVM> {
	name = 'AgtWkzkofferUiStore';
	fzwStore: KvAgtWkzgKofferFzwStore;
	bakStore: KvAgtWkzgKofferBakStore;
	agenturStore: AgenturStore;

	constructor(agenturStore: AgenturStore, fzwStore: KvAgtWkzgKofferFzwStore, bakStore: KvAgtWkzgKofferBakStore) {
		super();
		makeObservable(this);
		this.fzwStore = fzwStore;
		this.bakStore = bakStore;
		this.agenturStore = agenturStore;
	}

	async _baseLoad() {
		const fzws = await this.fzwStore.findAll();
		const baks = await this.bakStore.findAll();
		const agts = await this.agenturStore.findAll();
		const res: WkzgKofferVM[] = [];
		const years = [2025, 2026];
		for (const agt of agts) {
			for (const year of years) {
				const fzw = fzws.find((d) => d.agtId === agt.agtId && d.bm === year);
				const bak = baks.find((d) => d.agtId === agt.agtId && d.bm === year);
				const vm = new WkzgKofferVM(agt, fzw, bak, year);
				if (fzw || bak) {
					res.push(vm);
				}
			}
		}
		runInAction(() => {
			this.items = res;
		});
	}
}
